/**
 * detector.ts
 *
 * Runtime codec/decoder capability detection using the WebCodecs API.
 * Uses VideoDecoder.isConfigSupported() for accurate per-device results
 * without relying on User-Agent string sniffing.
 *
 * Reference approach: vdo.ninja/h265.html checkCodecSupport()
 *
 * Result mapping:
 *   'webcodecs' → VideoDecoder available + codec supported by hardware
 *   'wasm'      → WebCodecs unavailable or codec unsupported; fall back to
 *                 h265web.js Wasm decoder
 *
 * Note: H.264 'webcodecs' path is universal on all browsers that support
 * WebCodecs (Chrome 94+, Edge 94+, Safari 16.4+, Firefox 130+).
 * 'wasm' for H.265 covers Firefox, Safari on Intel, and old Android devices.
 */

export type DecoderType = 'webcodecs' | 'wasm';

const CODEC_STRINGS: Record<'h264' | 'h265', string> = {
  h264: 'avc1.42E01E', // H.264 Constrained Baseline Profile Level 3.0
  h265: 'hev1.1.6.L93.B0', // HEVC Main Profile Level 3.1, in-band params (Annex B)
};

function isDesktopLinux(): boolean {
  if (typeof navigator === 'undefined') return false;

  const candidates = [
    // userAgentData is the cleanest signal when available.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (navigator as any).userAgentData?.platform,
    navigator.platform,
    navigator.userAgent,
  ].filter((value): value is string => typeof value === 'string' && value.length > 0);

  return candidates.some((value) => /linux/i.test(value) && !/android/i.test(value));
}

/**
 * Detect which decoder to use for the given codec.
 *
 * @param codec        'h264' or 'h265'
 * @param codecString  Optional explicit codec string (e.g. 'avc1.640028' derived from
 *                     the actual SPS NAL). If omitted, the conservative default is used.
 * @returns 'webcodecs' if the browser supports hardware decode via WebCodecs,
 *          'wasm' otherwise
 */
export async function detectDecoderType(
  codec: 'h264' | 'h265',
  codecString?: string,
  description?: BufferSource,
): Promise<DecoderType> {
  // Allow forcing the WASM path (debug / regression testing of the h265web.js
  // pipeline on browsers where WebCodecs would otherwise be selected).
  // Set window.__VGPS_FORCE_WASM__ = true OR localStorage.VGPS_FORCE_WASM = '1'.
  try {
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const w = window as any;
      if (w.__VGPS_FORCE_WASM__) return 'wasm';
      if (window.localStorage?.getItem('VGPS_FORCE_WASM') === '1') return 'wasm';
      if (w.__VGPS_FORCE_WEBCODECS__) return 'webcodecs';
      if (window.localStorage?.getItem('VGPS_FORCE_WEBCODECS') === '1') return 'webcodecs';
    }
  } catch { /* ignore */ }

  // Linux H.265 is debugged against the h265web.js Wasm path first.
  // WebCodecs H.265 remains available via explicit force flags and is handled
  // in later Windows-focused phases.
  if (codec === 'h265' && isDesktopLinux()) return 'wasm';

  if (typeof VideoDecoder === 'undefined') return 'wasm';

  try {
    const config: VideoDecoderConfig = {
      codec: codecString ?? CODEC_STRINGS[codec],
      codedWidth: 1920,
      codedHeight: 1080,
    };
    if (description) config.description = description;
    const { supported } = await VideoDecoder.isConfigSupported(config);
    return supported ? 'webcodecs' : 'wasm';
  } catch {
    return 'wasm';
  }
}
